import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { FaCopy, FaSync, FaCheck } from 'react-icons/fa';

const RequestedOtpsPage = () => {
    const [otps, setOtps] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // State to force a re-subscription (Manual Refresh)
    const [refreshKey, setRefreshKey] = useState(0);
    
    // State to handle the "Copied!" tooltip logic per row
    const [copiedId, setCopiedId] = useState(null);

    // LIVE LISTENER for the last 10 OTPs
    useEffect(() => {
        setLoading(true);

        const q = query(
            collection(db, 'requested_otps'),
            orderBy('requested_at', 'desc'),
            limit(10)
        );

        // onSnapshot sets up a real-time stream
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const liveData = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    // Helper to format timestamp safely
                    formattedTime: data.requested_at?.toDate 
                        ? data.requested_at.toDate().toLocaleString() 
                        : (data.requested_at || 'Unknown')
                };
            });
            setOtps(liveData);
            
            // Add a small artificial delay so the user SEES the spinner even if the net is super fast
            setTimeout(() => {
                setLoading(false);
            }, 500);
            
        }, (error) => {
            console.error("Error listening to OTPs:", error);
            setLoading(false);
        });

        // Cleanup listener when component unmounts OR when refreshKey changes
        return () => unsubscribe();
    }, [refreshKey]);

    // --- HANDLERS ---
    
    const handleManualRefresh = () => {
        // Changing this key triggers the useEffect to re-run (disconnect & reconnect)
        setRefreshKey(prev => prev + 1);
    };

    const handleCopyOtp = (otp, recordId) => {
        if (!otp) return;
        
        navigator.clipboard.writeText(otp).then(() => {
            // Show "Copied" state for this specific row
            setCopiedId(recordId);
            
            // Revert back after 2 seconds
            setTimeout(() => {
                setCopiedId(null);
            }, 2000);
        });
    };

    return (
        <div className="page-container">
            <div className="page-header">
                <h1>Requested OTPs (Live)</h1>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    {/* Live Indicator */}
                    <div style={{ fontSize: '0.9rem', color: '#666', display: 'flex', alignItems: 'center' }}>
                        <span style={{ 
                            display: 'inline-block', 
                            width: '10px', 
                            height: '10px', 
                            backgroundColor: '#2ecc71', 
                            borderRadius: '50%', 
                            marginRight: '6px',
                            boxShadow: '0 0 5px #2ecc71'
                        }}></span>
                        Auto-updating
                    </div>

                    {/* Manual Refresh Button */}
                    <button 
                        onClick={handleManualRefresh}
                        className="button-secondary"
                        disabled={loading} // Prevent clicks while loading
                        style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '5px', 
                            padding: '6px 12px', 
                            fontSize: '0.85rem',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            opacity: loading ? 0.7 : 1
                        }}
                        title="Force Refresh List"
                    >
                        {/* We reuse the 'spin' animation keyframe that exists in your App.css 
                           for the spinner class. 
                        */}
                        <FaSync style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /> 
                        {loading ? 'Refreshing...' : 'Refresh List'}
                    </button>
                </div>
            </div>

            <div className="data-table-container">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Email Address</th>
                            <th>OTP Code</th>
                            <th>Requested At</th>
                            <th>ID</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && otps.length === 0 ? (
                             /* Only show full table loader if we have NO data */
                            <tr><td colSpan="4" className="no-data-cell">Loading OTPs...</td></tr>
                        ) : otps.length > 0 ? (
                            otps.map((record) => (
                                <tr key={record.id}>
                                    <td style={{ fontWeight: '600', color: '#2c3e50' }}>
                                        {record.email}
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <span style={{ 
                                                backgroundColor: '#e3f2fd', 
                                                color: '#0d47a1', 
                                                padding: '4px 8px', 
                                                borderRadius: '4px', 
                                                fontFamily: 'monospace',
                                                fontWeight: 'bold',
                                                fontSize: '1.1em',
                                                letterSpacing: '1px'
                                            }}>
                                                {record.otp}
                                            </span>

                                            {/* COPY BUTTON */}
                                            <button 
                                                onClick={() => handleCopyOtp(record.otp, record.id)}
                                                style={{
                                                    background: 'none',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    padding: '4px 8px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '5px',
                                                    color: copiedId === record.id ? '#2ecc71' : '#666',
                                                    fontSize: '0.8rem',
                                                    transition: 'all 0.2s',
                                                    minWidth: '70px',
                                                    justifyContent: 'center'
                                                }}
                                                title="Copy to clipboard"
                                            >
                                                {copiedId === record.id ? <FaCheck /> : <FaCopy />}
                                                {copiedId === record.id ? "Copied" : "Copy"}
                                            </button>
                                        </div>
                                    </td>
                                    <td style={{ color: '#555' }}>
                                        {record.formattedTime}
                                    </td>
                                    <td style={{ fontSize: '0.8rem', color: '#999' }}>
                                        {record.id}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr><td colSpan="4" className="no-data-cell">No OTP requests found.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default RequestedOtpsPage;